import React from "react";

import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import { ContractPlanComponent } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";

interface ContractPlanComponentsRecordProps {
  contractPlanComponents: ContractPlanComponent[];
  contractsTabColSpan: number;
  isExpanded: boolean;
  currency: string;
  // Maps price-point id (and componentId as a fallback) to a price in cents.
  // Used to fill in the price when priceInCents is 0 (e.g. Zuora orgs).
  priceOverrides: Map<number, number>;
}

const ContractPlanComponentsRecord = ({
  contractPlanComponents,
  contractsTabColSpan,
  isExpanded,
  currency,
  priceOverrides,
}: ContractPlanComponentsRecordProps) => {
  const displayComponentHeadings = () => {
    const headings: Array<{ title: string; key: string }> = [
      {
        title: "Name",
        key: "contract-plan-components-heading-name",
      },
      {
        title: "Price Name",
        key: "contract-plan-components-heading-price-name",
      },
      { title: "Price", key: "contract-plan-components-heading-price" },
      { title: "Quantity", key: "contract-plan-components-heading-quantity" },
      {
        title: "Coupon Codes",
        key: "contract-plan-components-heading-coupon-codes",
      },
    ];

    return headings.map(({ title, key }) => (
      <TableCell key={key}>
        <b>{title}</b>
      </TableCell>
    ));
  };

  const displayComponentRecord = ({
    component,
  }: {
    component: ContractPlanComponent;
  }) => {
    const {
      id,
      componentId,
      quantity,
      priceId,
      couponCodes,
      componentName,
      pricePointName,
      priceInCents,
    } = component;
    const overridePrice =
      priceOverrides.get(priceId) ?? priceOverrides.get(componentId);
    const effectivePriceInCents =
      priceInCents > 0 ? priceInCents : (overridePrice ?? priceInCents);
    const price = effectivePriceInCents / 100;
    const formattedPrice = currency
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 6,
        }).format(price)
      : price;
    const componentIDText = `Component ID: ${componentId}`;

    const priceIDText = `Price ID: ${priceId}`;
    return (
      <TableRow key={id} data-testid={`contract-plan-component-row-${id}}`}>
        <Tooltip
          title={componentIDText}
          data-testid={"contract-plan-component-component-id"}
        >
          <TableCell data-testid={"contract-plan-component-name"}>
            {componentName}
          </TableCell>
        </Tooltip>
        <Tooltip
          title={priceIDText}
          data-testid={"contract-plan-component-price-id"}
        >
          <TableCell data-testid={"contract-plan-component-price-name"}>
            {pricePointName}
          </TableCell>
        </Tooltip>
        <TableCell data-testid={"contract-plan-component-price"}>
          {formattedPrice}
        </TableCell>
        <TableCell data-testid={"contract-plan-component-quantity"}>
          {quantity}
        </TableCell>
        <TableCell data-testid={"contract-plan-component-coupon-codes"}>
          {couponCodes.length > 0 ? couponCodes.join(", ") : "N/A"}
        </TableCell>
      </TableRow>
    );
  };

  const populatePlanComponents = () => {
    if (contractPlanComponents.length == 0) {
      return (
        <TableRow>
          <TableCell
            data-testid={"contract-plan-component-no-results"}
            colSpan={contractsTabColSpan}
          >
            <b>{"No components found."}</b>
          </TableCell>
        </TableRow>
      );
    }

    return contractPlanComponents.map((component: ContractPlanComponent) => {
      return <>{displayComponentRecord({ component })}</>;
    });
  };

  return (
    <>
      <TableCell
        style={{ paddingBottom: 0, paddingTop: 0 }}
        colSpan={contractsTabColSpan}
      >
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 1 }}>
            <Typography
              variant="body1"
              gutterBottom
              component="span"
              data-testid={"contract-plan-component-table-title"}
            >
              <b>Components</b>
            </Typography>
            <Table size="small" aria-label="contract-plan-components-table">
              <TableHead>
                <TableRow>{displayComponentHeadings()}</TableRow>
              </TableHead>
              <TableBody data-testid={"contract-plan-components-records"}>
                {populatePlanComponents()}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </>
  );
};

export default ContractPlanComponentsRecord;
